/**
 * Contrato HTTP entre `apps/api` e `apps/web`. Fonte única: se o front e o back
 * discordarem, o typecheck quebra nos dois.
 *
 * Datas viajam como ISO-8601 em UTC (`string`), nunca `Date` — JSON não tem Date,
 * e converter na borda é mais barato que descobrir um `"[object Object]"` em produção.
 */

export type Iso = string;
export type Uuid = string;

// ── Identidade ──────────────────────────────────────────────────────────────

export type Role = 'owner' | 'admin' | 'doctor' | 'attendant' | 'viewer';

/**
 * Quem está na sessão.
 *
 * Sem `visibleDoctorIds`: a lista viajava daqui para o navegador e NENHUMA tela a lia.
 * Quem decide o que a atendente vê é `app.visible_doctors()` dentro do banco, por
 * política de RLS — uma cópia no cliente não filtra nada e envelhece no `localStorage`
 * até o próximo login, então ela só podia discordar da verdade.
 *
 * `role` está aqui porque vem do token, e ainda não decide nada: não há checagem de
 * papel em rota nem em tela. RBAC é trabalho de M1 (`docs/domain/identity.md`), e até
 * lá esconder botão por papel seria fingir uma permissão que o servidor não impõe.
 */
export interface Me {
  readonly userId: Uuid;
  readonly fullName: string;
  readonly email: string;
  /** O tenant é a OPERAÇÃO da atendente. Cada médico é um cliente dentro dele. */
  readonly tenantId: Uuid;
  readonly tenantName: string;
  readonly role: Role;
  /** Senha temporária, posta pelo administrador. A tela não abre nada antes da troca. */
  readonly mustChangePassword?: boolean;
  /** O nome que aparece para a equipe na conversa. `null` = o nome completo. */
  readonly displayName?: string | null;
  /** As equipes de que a pessoa é membro ativo, e o papel dela em cada uma. */
  readonly teams?: readonly MyTeam[];
  /** Convites de equipe esperando resposta: a faixa no topo da tela. */
  readonly pendingInvites?: number;
}

export interface MyTeam {
  readonly id: Uuid;
  readonly name: string;
  readonly role: TeamRole;
}

/** O foco do inbox. `null` = todos os médicos que a pessoa enxerga. */
/** Para silenciar fora do turno o que não é da casa (docs/domain/equipes.md §11). */
export interface MeuPlantao {
  readonly temTurno: boolean;
  readonly dePlantao: boolean;
}

export interface InboxFocus {
  readonly doctorIds: readonly Uuid[] | null;
}

export interface PerfilUpdate {
  readonly fullName?: string;
  /** Vazio ou `null` volta para o nome completo. */
  readonly displayName?: string | null;
}

// ── Equipes entre operações (docs/domain/equipes.md) ────────────────────────

/** O papel na equipe. `owner` é o líder: um por equipe. */
export type TeamRole = 'owner' | 'admin' | 'member';
/** O nível de um médico compartilhado. Canal, cartão e cadastro ficam sempre com a dona. */
export type ShareAccess = 'atendimento' | 'gestao';

export interface TeamMember {
  readonly userId: Uuid;
  readonly name: string;
  /** Só para membros da mesma equipe: é como se convida e se reconhece alguém. */
  readonly email: string | null;
  readonly role: TeamRole;
  readonly joinedAt: Iso;
  readonly isMe: boolean;
  /** Quantos médicos a operação desta pessoa compartilha com a equipe. */
  readonly sharedDoctors: number;
}

export interface TeamInvite {
  readonly id: Uuid;
  readonly email: string;
  readonly role: Exclude<TeamRole, 'owner'>;
  readonly invitedByName: string;
  readonly createdAt: Iso;
  readonly expiresAt: Iso;
}

/** Um convite que chegou para mim. */
export interface IncomingInvite {
  readonly id: Uuid;
  readonly teamId: Uuid;
  readonly teamName: string;
  readonly role: Exclude<TeamRole, 'owner'>;
  readonly invitedByName: string;
  readonly expiresAt: Iso;
}

export interface TeamShare {
  readonly clientId: Uuid;
  readonly clientName: string;
  readonly specialty: string | null;
  readonly access: ShareAccess;
  /** Quem trouxe o médico. `null` quando é da minha operação. */
  readonly ownerName: string | null;
  readonly mine: boolean;
  readonly sharedByName: string;
  readonly sharedAt: Iso;
  /**
   * A declaração nomeou um líder que não é mais o líder (§9): o atendimento segue,
   * e o líder novo não vê o financeiro até a dona declarar de novo.
   */
  readonly needsReattest: boolean;
}

/** Um médico da minha operação que ainda não está nesta equipe. */
export interface ShareCandidate {
  readonly clientId: Uuid;
  readonly clientName: string;
  readonly specialty: string | null;
  readonly status: ClientStatus;
}

export interface TeamSummary {
  readonly id: Uuid;
  readonly name: string;
  readonly myRole: TeamRole;
  readonly leaderName: string;
  readonly members: number;
  readonly sharedDoctors: number;
}

export interface TeamDetail extends TeamSummary {
  readonly leaderId: Uuid;
  readonly memberList: readonly TeamMember[];
  /** Só para líder e admin. */
  readonly invites: readonly TeamInvite[];
  readonly shares: readonly TeamShare[];
  /** Vazio para quem não é owner ou admin da própria operação: só eles compartilham. */
  readonly candidates: readonly ShareCandidate[];
  readonly canShare: boolean;
  readonly sharePolicy: {
    readonly mode: 'all' | 'selected';
    readonly defaultAccess: ShareAccess;
  } | null;
}

export interface CreateTeamRequest {
  readonly name: string;
}

export interface TeamInviteRequest {
  readonly email: string;
  readonly role?: Exclude<TeamRole, 'owner'>;
}

/** O termo de confidencialidade vigente (docs/termos), e o que o aceite precisa repetir. */
export interface TeamTerm {
  readonly id: Uuid;
  readonly version: number;
  readonly bodyMd: string;
  readonly sha256: string;
}

export interface AcceptInviteRequest {
  readonly termId: Uuid;
  /** O hash do texto que a tela mostrou. O servidor confere contra o do termo. */
  readonly termSha256: string;
}

export interface DeclineInviteRequest {
  /** "Não receber mais convites desta equipe." */
  readonly block?: boolean;
}

export interface UpdateMemberRequest {
  readonly role: Exclude<TeamRole, 'owner'>;
}

export interface TransferLeadershipRequest {
  readonly userId: Uuid;
}

/** A declaração "o médico autorizou", com os marcadores já preenchidos para a tela. */
export interface AttestationText {
  readonly id: Uuid;
  readonly version: number;
  readonly bodyMd: string;
}

export interface ShareRequest {
  readonly clientIds: readonly Uuid[];
  readonly access: ShareAccess;
  /** A versão da declaração que a tela mostrou e a pessoa confirmou. */
  readonly attestationTextId: Uuid;
}

export interface UpdateShareRequest {
  readonly access: ShareAccess;
  /** Obrigatória para subir o nível ou para declarar de novo depois de trocar o líder. */
  readonly attestationTextId?: Uuid;
}

export interface SharePolicyRequest {
  readonly mode: 'all' | 'selected';
  readonly defaultAccess: ShareAccess;
}

/** Um turno da grade semanal. Horas no fuso canônico, `America/Sao_Paulo`. */
export interface ShiftRule {
  readonly id: Uuid;
  readonly userId: Uuid;
  readonly name: string;
  /** 0 = domingo. */
  readonly weekday: number;
  readonly startTime: string;
  readonly endTime: string;
  /** `null` = todos os médicos da equipe. */
  readonly doctorIds: readonly Uuid[] | null;
}

export interface ShiftOverride {
  readonly id: Uuid;
  readonly userId: Uuid;
  readonly name: string;
  readonly kind: 'extra' | 'folga';
  readonly startsAt: Iso;
  readonly endsAt: Iso;
}

export interface TeamShifts {
  readonly rules: readonly ShiftRule[];
  readonly overrides: readonly ShiftOverride[];
  /** Quem está de plantão agora. */
  readonly onDutyNow: readonly Uuid[];
  /** Se conversa nova sem dono vai sozinha para quem está de turno. */
  readonly autoAssign: boolean;
  readonly canManage: boolean;
}

export interface ShiftRuleRequest {
  readonly userId?: Uuid;
  readonly weekday: number;
  readonly startTime: string;
  readonly endTime: string;
  readonly doctorIds?: readonly Uuid[] | null;
}

export interface ShiftOverrideRequest {
  readonly userId?: Uuid;
  readonly startsAt: Iso;
  readonly endsAt: Iso;
  readonly kind: 'extra' | 'folga';
}

/** Quem fez o quê (docs/domain/equipes.md §6). Sem corpo, nome de paciente ou telefone. */
export interface ActivityEntry {
  readonly id: Uuid;
  readonly occurredAt: Iso;
  readonly action:
    | 'enviou_mensagem'
    | 'enviou_template'
    | 'enviou_midia'
    | 'escreveu_nota'
    | 'abriu_conversa'
    | 'resolveu_conversa'
    | 'reabriu_conversa'
    | 'assumiu_conversa'
    | 'soltou_conversa'
    | 'etiquetou_conversa'
    | 'tirou_etiqueta'
    | 'marcou_consulta'
    | 'desmarcou_consulta'
    | 'remarcou_consulta'
    | 'confirmou_consulta'
    | 'registrou_presenca'
    | 'editou_ficha'
    | 'cadastrou_paciente'
    | 'cadastrou_dependente'
    | 'editou_dependente'
    | 'mudou_agenda'
    | 'criou_equipe'
    | 'convidou'
    | 'entrou_na_equipe'
    | 'saiu_da_equipe'
    | 'removeu_da_equipe'
    | 'mudou_papel'
    | 'transferiu_lideranca'
    | 'compartilhou_medico'
    | 'revogou_medico'
    | 'declarou_autorizacao';
  readonly targetType: string;
  readonly targetId: Uuid | null;
  readonly clientId: Uuid | null;
  readonly clientName: string | null;
  readonly actorName: string;
  readonly mine: boolean;
  /** A pessoa chegou ao médico por uma equipe, e não pela casa dele. */
  readonly viaTeam: boolean;
}

/** Assumir (`userId` = eu), passar para alguém, ou soltar (`null`). */
export interface AssignRequest {
  readonly userId: Uuid | null;
  /** Assumir mesmo estando com outra pessoa. Sem isto, 409 diz com quem está. */
  readonly takeOver?: boolean;
}

// ── Clientes: os médicos que a atendente atende ─────────────────────────────

export type ClientStatus = 'active' | 'paused' | 'churned';

/**
 * Quem anexa o cartão à WABA deste cliente.
 *
 * `client` — o próprio médico. É o caminho que a Meta desenha e o padrão.
 * `attendant` — a atendente foi adicionada ao portfolio do cliente com permissão
 * financeira e usa o cartão dela, embutindo o custo na mensalidade.
 *
 * Em nenhum dos dois casos a Zenda paga: Tech Provider não tem linha de crédito
 * (ADR 0011). E em nenhum dos dois o cliente deixa de ser dono dos ativos — a Meta
 * impõe isso ✅ *"you cannot restrict this access in any way"*.
 *
 * É dado e não convenção porque muda o TEXTO que a atendente manda ao médico e o
 * passo a passo da tela. Pedir cartão a quem já combinou que não paga é como o
 * onboarding trava sem ninguém entender por quê.
 */
export type PaymentOwner = 'client' | 'attendant';

/**
 * Quem é a fonte de verdade da agenda deste cliente.
 *
 * `zenda` é o padrão e o caminho próprio: a agenda é nossa, e nós a garantimos.
 *
 * Uma plataforma de terceiro inverte isso. A Zenda passa a ser **interface**: a
 * reserva nasce lá, e daqui sai só a disponibilidade — como *guardrail* de quando
 * pode marcar. Não são duas agendas mescladas; é uma agenda, com um dono
 * declarado. Ver [ADR 0017](../../../docs/adr/0017-a-agenda-de-um-cliente-tem-um-dono.md).
 */
export type AgendaSource = 'zenda' | 'doctoralia';

/**
 * O centro do produto. É aqui que a atendente sabe quanto cobra de cada cliente,
 * quando vence, se o WhatsApp daquele médico ainda está conectado, e quanto de
 * trabalho aquele cliente deu no mês.
 *
 * **Um cliente é um médico** ([ADR 0019](../../../docs/adr/0019-um-cliente-e-um-medico.md)).
 * `name` é o nome dele, com um registro espelho em `doctors` — o canal pertence ao
 * médico, não ao cliente, e é isso que mantém as duas tabelas.
 *
 * Uma clínica entra como vários clientes, um por médico, e **não** como um cliente
 * com vários médicos: o eixo cliente→médicos tinha um valor só na prática e cobrava
 * um seletor em cada tela.
 */
export interface Client {
  readonly id: Uuid;
  /** O médico deste cliente (ADR 0019), que é a unidade do foco do inbox. */
  readonly doctorId: Uuid | null;
  readonly name: string;
  /** Servida de `doctors.specialty`: um cliente é um médico (ADR 0019). */
  readonly specialty: string | null;
  /**
   * Servido de `doctors.crm`.
   *
   * Está aqui, e não só em `DoctorInClient`, porque o formulário de edição precisa
   * dele para vir preenchido: um campo de PATCH que abre vazio apaga o valor de quem
   * salvar sem tocá-lo.
   */
  readonly crm: string | null;
  readonly status: ClientStatus;
  readonly color: string;

  readonly monthlyFeeBrl: number | null;
  readonly paymentDay: number | null;
  readonly contractStartedOn: Iso | null;
  readonly contactName: string | null;
  readonly contactPhone: string | null;

  /** De quem é o cartão anexado à WABA deste cliente. Muda todo o roteiro de conexão. */
  readonly paymentOwner: PaymentOwner;

  /**
   * Qual sistema é a agenda deste cliente. Muda o que a tela de Agenda faz: em
   * plataforma de terceiro ela é interface, e a marcação vai para lá antes de
   * existir aqui.
   */
  readonly agendaSource: AgendaSource;

  /** Saúde da conexão. É o que quebra sozinho e precisa ser restabelecido. */
  readonly channels: readonly Channel[];

  /** Trabalho do mês corrente — o que sustenta a conversa sobre reajuste. */
  readonly patientsServedThisMonth: number;
  readonly conversationsOpen: number;
  readonly appointmentsThisMonth: number;

  /** Cobrança em aberto mais próxima, se houver. */
  readonly nextPaymentDueOn: Iso | null;
  readonly nextPaymentAmountBrl: number | null;
  readonly paymentOverdue: boolean;

  /**
   * Como quem pergunta chega a este cliente (docs/domain/equipes.md §4.2).
   *
   * `casa` é a operação dona; `gestao` e `atendimento` vêm de uma equipe. O servidor
   * já recusa o que o nível não permite; a tela usa isto para não oferecer.
   */
  readonly access: ClientAccess;
  /** Quem trouxe o cliente, para o selo "da Ana". `null` na casa. */
  readonly ownerName: string | null;
  /**
   * Se a mensalidade e os pagamentos vieram. Fora da casa, só o líder da equipe os vê,
   * e só depois de a dona declarar com o nome dele (§8): `false` não é "sem contrato".
   */
  readonly financeVisible: boolean;
}

export type ClientAccess = 'casa' | 'gestao' | 'atendimento';

export interface ClientUpsertRequest {
  /** O nome do médico. Vira também o `full_name` do registro em `doctors`. */
  readonly name: string;
  /** Gravada em `doctors.specialty`, que é o dono do dado. */
  readonly specialty?: string | null;
  /**
   * O registro no conselho, como o médico o escreve: "CRM-SP 92.771".
   *
   * Já vem com a sigla, e a tela não prefixa nada — "CRM CRM-SP" foi o resultado da
   * primeira tentativa. O campo existia em `doctors` e era exibido no detalhe do
   * cliente sem que nenhuma tela pudesse preenchê-lo: só o seed gravava.
   */
  readonly crm?: string | null;
  readonly monthlyFeeBrl?: number | null;
  readonly paymentDay?: number | null;
  readonly contactName?: string | null;
  readonly contactPhone?: string | null;
  readonly paymentOwner?: PaymentOwner;
  readonly agendaSource?: AgendaSource;
  /**
   * Só na edição. Pausar e encerrar precisam existir: um cliente que sai e não pode
   * ser marcado como `churned` mantém a receita mensal errada para sempre — e o número
   * do painel deixa de ser confiável, que é pior do que não ter o número.
   */
  readonly status?: ClientStatus;
}

/**
 * Início da conexão do WhatsApp de um médico.
 *
 * Em produção isto abre o Embedded Signup v4 da Meta e devolve o `code` (TTL de 30 s),
 * que é trocado no servidor. Enquanto o App Review não sai, a rota devolve o roteiro
 * do que vai acontecer — e o ambiente de demonstração simula a conexão, deixando isso
 * explícito na resposta para ninguém confundir demo com integração real.
 */
export interface StartConnectionResponse {
  /**
   * `test_number` é o número que a própria Meta provisionou para este app.
   * Não é maquete: envia e recebe de verdade. O que ele não é é o número do
   * médico — esse só chega pelo Embedded Signup, que exige o App Review
   * aprovado.
   */
  readonly mode: 'embedded_signup' | 'simulated' | 'test_number';
  readonly channelId: Uuid | null;
  readonly message: string;
  readonly steps: readonly string[];
}

/**
 * O que o navegador precisa para abrir o Embedded Signup. Nada aqui é segredo:
 * `appId` e `configId` viajam no JavaScript que monta o fluxo.
 *
 * `available` é `false` quando o ambiente não tem a integração real ligada — e aí a
 * tela mostra o roteiro em vez de um botão que abre um fluxo quebrado.
 */
/**
 * Por onde o número envia. `gupshup` é o Solution Partner que paga a Meta, e com ele o
 * médico não cadastra cartão ([ADR 0029](../../../docs/adr/0029-a-gupshup-paga-a-meta-e-o-medico-nao-poe-cartao.md)).
 */
export type WhatsappProvider = 'meta' | 'gupshup';

export interface MetaSignupConfig {
  readonly available: boolean;
  /** Por onde vão os NOVOS convites deste ambiente. */
  readonly provider: WhatsappProvider;
  readonly appId: string | null;
  readonly configId: string | null;
  readonly graphVersion: string;
}

/**
 * Um passo do funil de onboarding, inclusive a desistência.
 *
 * Existe porque "o médico não conseguiu conectar" não diz onde ele parou, e o passo em
 * que ele desistiu é exatamente o que precisa ser consertado.
 */
export interface MetaSessionEvent {
  readonly doctorId?: Uuid;
  readonly clientId?: Uuid;
  readonly sessionId?: string;
  readonly currentStep?: string;
  readonly status: 'started' | 'finished' | 'cancelled' | 'failed';
  readonly errorCode?: string;
  readonly errorMessage?: string;
  readonly wabaId?: string;
  readonly phoneNumberId?: string;
  readonly businessId?: string;
}

/**
 * O `code` tem TTL de 30 s ✅ — esta requisição sai assim que o fluxo fecha, sem
 * confirmação intermediária e sem fila.
 */
export interface MetaOnboardRequest {
  readonly code: string;
  readonly wabaId: string;
  readonly phoneNumberId: string;
  readonly doctorId: Uuid;
  readonly sessionId?: string;
  /** Qual `FINISH` a janela da Meta emitiu. So desempata quando a Graph API nao diz. */
  readonly modo?: 'cloud_api' | 'coexistence';
}

/**
 * O link que a secretária manda para o médico.
 *
 * Existe porque quem opera o Zenda e quem precisa autorizar na Meta são pessoas
 * diferentes, em lugares diferentes. Uso único, e o segredo aparece **uma vez**: quem o
 * perder pede outro, e o anterior morre no mesmo gesto.
 */
export interface ConnectLink {
  readonly url: string;
  readonly expiresAt: Iso;
}

/**
 * O que a página do convite pode mostrar a quem não tem sessão: o nome do médico e o de
 * quem pediu, e nada mais. Sem paciente, sem agenda, sem os outros médicos da operação.
 */
export interface ConviteInfo {
  readonly doctorName: string;
  readonly operationName: string;
  readonly paymentOwner: PaymentOwner;
  readonly state: 'ok' | 'expirado' | 'usado';
  readonly provider: WhatsappProvider;
}

/** O resultado de conferir com a Gupshup se o médico terminou a conexão. */
export interface ConnectCheck {
  readonly estado: 'ativado' | 'ja_ativo' | 'pendente' | 'parado' | 'sem_convite';
  readonly channelId?: Uuid;
}

export interface MetaOnboardResult {
  readonly channelId: Uuid;
  /** Coexistence: o numero continua funcionando no celular do medico. */
  readonly coexistence: boolean;
  /**
   * O historico foi pedido a Meta.
   *
   * `false` com `coexistence: true` e o caso que a tela NAO pode esconder: a janela de
   * 24 h e de tiro unico, entao o passado daquele medico se perdeu. Nao da para tentar
   * de novo depois.
   */
  readonly historySynced: boolean;
  /**
   * `false` significa canal criado e inerte: sem inscrição confirmada nenhuma mensagem
   * chega, e a falha não emite erro nenhum. A tela é obrigada a dizer isso.
   */
  readonly webhookSubscribed: boolean;
  /**
   * O numero foi registrado na Cloud API. `null` em Coexistence, que nao registra;
   * `false` e numero que recebe e nao envia.
   */
  readonly phoneRegistered: boolean | null;
  readonly state: 'active' | 'verifying';
  readonly message: string;
}

/**
 * Cobrança é **lembrete**, não faturamento.
 *
 * Os médicos não pagam o Zenda — quem paga é a atendente. O que ela precisa daqui é
 * lembrar de cobrar cada cliente no dia certo e marcar quando recebeu. Nada de boleto,
 * nota fiscal ou régua de inadimplência: isso seria construir um ERP para resolver
 * uma anotação de caderno.
 */
/**
 * A mensalidade nao tem id porque nao existe como linha ate ser paga: o
 * calendario e derivado de `paymentDay` + `monthlyFeeBrl` do cliente. O mes de
 * referencia E a chave.
 */
export interface Payment {
  readonly referenceMonth: Iso;
  readonly amountBrl: number;
  readonly dueOn: Iso;
  readonly paidOn: Iso | null;
  readonly overdue: boolean;
}

export interface DoctorInClient {
  readonly id: Uuid;
  readonly fullName: string;
  readonly specialty: string | null;
  readonly crm: string | null;
  readonly color: string;
  readonly channel: Channel | null;
}

/** Página de detalhe de um cliente. */
/**
 * Volume de um mês fechado do cliente.
 *
 * Contagem, nunca identidade: aqui não entra `contact_id`, nome nem telefone. É
 * o que permite este número existir numa tela de tendência sem virar dado de
 * saúde.
 */
export interface MonthlyVolume {
  /** Primeiro dia do mês, em ISO. */
  readonly month: string;
  readonly patients: number;
  readonly appointments: number;
}

export interface ClientDetail {
  readonly client: Client;
  /**
   * O médico do cliente. `null` só em cliente legado que nasceu como clínica e
   * ficou sem nenhum — a tela precisa saber mostrar esse caso, senão ele vira
   * uma página quebrada em vez de um convite a consertar.
   */
  readonly doctor: DoctorInClient | null;
  readonly payments: readonly Payment[];
  readonly conversationsOpen: number;
  readonly patientsTotal: number;
  /** Três meses, do mais antigo ao atual. Mês sem movimento vem com zero. */
  readonly monthly: readonly MonthlyVolume[];
}

// ── Pacientes: quem já falou com o cliente ──────────────────────────────────

/**
 * Um paciente é um CONTATO que já conversou com aquele cliente. Não há cadastro
 * manual e não deve haver: ele nasce sozinho quando a pessoa manda a primeira
 * mensagem, identificado pelo número. Pedir para a atendente cadastrar paciente à mão
 * seria criar trabalho para produzir o que a plataforma já sabe.
 *
 * A ficha é por **(cliente, paciente)**: a mesma pessoa atendida por dois clientes tem
 * duas fichas, e a anotação de um médico não alcança a tela do outro.
 */
export interface PatientSummary {
  readonly contactId: Uuid;
  readonly name: string;
  readonly phone: string | null;
  readonly waId: string;
  /** O WhatsApp já confirmou o número: ele não muda mais à mão (ADR 0028). */
  readonly phoneLocked: boolean;

  readonly clientId: Uuid;
  readonly clientName: string;
  readonly clientColor: string;

  /** Conversa mais recente dele com este cliente — o atalho para responder. */
  readonly conversationId: Uuid | null;
  readonly lastMessageAt: Iso | null;
  readonly firstSeenAt: Iso | null;
  readonly conversationCount: number;
  readonly appointmentCount: number;

  /** Ficha. Tudo opcional: ela começa vazia e cresce com o atendimento. */
  readonly birthDate: Iso | null;
  /**
   * A próxima consulta marcada, se houver.
   *
   * Substitui o "retorno" da ficha: era uma data paralela, digitada à mão, que
   * a agenda contradizia no dia seguinte. A agenda é a fonte.
   */
  readonly nextAppointmentAt: Iso | null;
  readonly nextAppointmentTitle: string | null;
  /** O dependente da próxima consulta; `null` quando ela é do próprio paciente. */
  readonly nextAppointmentFor: string | null;

  /** Quando a atendente declarou o opt-in do WhatsApp para este médico (ADR 0028). */
  readonly optInAt: Iso | null;

  /** Os dependentes ativos deste paciente com este médico. */
  readonly dependents: readonly { readonly id: Uuid; readonly name: string }[];
}

/** A ficha aberta: tudo que sabemos, mais o histórico. */
export interface PatientDetail {
  readonly patient: PatientSummary;
  readonly notes: string | null;
  readonly record: PatientRecordFields;
  /** Inclui os arquivados, que continuam nas consultas antigas. */
  readonly dependents: readonly PatientDependent[];
  readonly appointments: readonly Appointment[];
  readonly conversations: readonly {
    readonly id: Uuid;
    readonly status: ConversationStatus;
    readonly lastMessageAt: Iso | null;
    readonly lastMessagePreview: string | null;
    readonly doctorName: string;
  }[];
}

export type PatientSex = 'F' | 'M' | 'I';

export type DependentRelationship =
  'filho' | 'enteado' | 'neto' | 'sobrinho' | 'tutelado' | 'conjuge' | 'pai_mae' | 'outro';

/**
 * Quem é atendido sem ser o dono do WhatsApp: o filho de uma paciente (ADR 0028). É do
 * responsável E do médico: o mesmo filho com dois médicos tem dois cadastros.
 */
export interface PatientDependent {
  readonly id: Uuid;
  readonly name: string;
  readonly socialName: string | null;
  readonly birthDate: Iso | null;
  readonly sex: PatientSex | null;
  readonly relationship: DependentRelationship;
  readonly cpf: string | null;
  readonly insurer: string | null;
  readonly insurancePlan: string | null;
  readonly insuranceCardNumber: string | null;
  readonly insuranceValidUntil: Iso | null;
  readonly notes: string | null;
  readonly archivedAt: Iso | null;
  /** Consultas marcadas daqui para frente: o aviso antes de arquivar. */
  readonly upcomingAppointments: number;
}

export interface DependentUpsert {
  readonly name?: string;
  readonly socialName?: string | null;
  readonly birthDate?: Iso | null;
  readonly sex?: PatientSex | null;
  readonly relationship?: DependentRelationship;
  readonly cpf?: string | null;
  readonly insurer?: string | null;
  readonly insurancePlan?: string | null;
  readonly insuranceCardNumber?: string | null;
  readonly insuranceValidUntil?: Iso | null;
  readonly notes?: string | null;
  /** `true` arquiva, `false` desarquiva. */
  readonly archived?: boolean;
}

/**
 * O que a ficha pode guardar além de nascimento e anotações (ADR 0028). Nada é
 * obrigatório. DADO PESSOAL: nenhum destes campos entra em log, trace ou evento.
 */
export interface PatientRecordFields {
  readonly socialName: string | null;
  readonly sex: PatientSex | null;
  /** Só dígitos, conferido pelo dígito verificador. */
  readonly cpf: string | null;
  readonly email: string | null;
  readonly phoneSecondary: string | null;
  /** Só dígitos. */
  readonly cep: string | null;
  readonly street: string | null;
  readonly streetNumber: string | null;
  readonly complement: string | null;
  readonly district: string | null;
  readonly city: string | null;
  readonly uf: string | null;
  readonly insurer: string | null;
  readonly insurancePlan: string | null;
  readonly insuranceCardNumber: string | null;
  readonly insuranceValidUntil: Iso | null;
  /** Como conheceu o médico. */
  readonly referralSource: string | null;
}

export interface PatientRecordUpdate extends Partial<PatientRecordFields> {
  readonly displayName?: string | null;
  /** Só muda enquanto a Meta não confirmou o número (o `wa_id` ainda é provisório). */
  readonly phone?: string;
  readonly birthDate?: Iso | null;
  readonly notes?: string | null;
  /** Declara o opt-in; `null` retira a declaração. */
  readonly optInMethod?: OptInMethod | null;
}

/** Como a atendente coletou o opt-in do WhatsApp. A Meta aceita meio offline ✅. */
export type OptInMethod = 'presencial' | 'telefone' | 'papel' | 'outro';

/**
 * Cadastro manual de paciente (ADR 0028). O telefone é obrigatório: é por ele que a
 * primeira mensagem dessa pessoa cai neste mesmo paciente, e não num novo.
 */
export interface PatientCreate {
  readonly name: string;
  readonly phone: string;
  readonly optInMethod?: OptInMethod | null;
  readonly birthDate?: Iso | null;
  readonly notes?: string | null;
}

export interface PatientCreated {
  readonly contactId: Uuid;
  /**
   * O número já era paciente DESTE médico. Paciente de outro médico não conta: entre
   * médicos o cadastro não revela nada.
   */
  readonly alreadyLinked: boolean;
}

// ── Procedimentos do cliente ────────────────────────────────────────────────

// ── Tags de conversa ────────────────────────────────────────────────────────

/** Rótulos da ATENDENTE, não do cliente: ela organiza o trabalho dela. */
export interface ConversationTag {
  readonly id: Uuid;
  readonly name: string;
  readonly color: string;
  readonly usageCount: number;
}

// ── Console de verificação (App Review) ─────────────────────────────────────

/**
 * As duas telas que existem para gravar os screencasts que a Meta exige — uma
 * por permissão. ⚠️ Um vídeo por permissão: mostrar as duas no mesmo vídeo é
 * causa documentada de rejeição.
 *
 * Usam o NÚMERO DE TESTE da Meta, que funciona sem App Review para até cinco
 * destinatários verificados. É o que permite gravar ANTES da aprovação, em vez
 * de esperar por ela — sem isso o processo seria circular.
 */
export interface ReviewStatus {
  readonly ready: boolean;
  readonly phoneNumberId: string | null;
  readonly wabaId: string | null;
  readonly graphVersion: string;
  /** Destinatários já verificados no painel, quando conseguimos lê-los. */
  readonly allowedRecipients: readonly string[];
}

/**
 * `text` só funciona com a janela de 24 h aberta — o paciente precisa ter
 * escrito antes. Fora dela, e o primeiro contato está sempre fora dela, a
 * Cloud API recusa texto livre e só aceita template aprovado.
 */
/**
 * Um template de mensagem, como a Meta o devolve.
 *
 * Templates sao ativos do WABA — ou seja, de CADA CLIENTE, nao do Zenda ✅. Por
 * isso a listagem e por cliente: dois medicos podem ter o mesmo "lembrete de
 * consulta" com estados de aprovacao diferentes, e a atendente precisa ver o de
 * quem ela esta atendendo.
 */
export interface MessageTemplate {
  readonly id: string;
  readonly name: string;
  readonly language: string;
  readonly category: string;
  /** APPROVED, PENDING, REJECTED, PAUSED, DISABLED — o que a Meta devolver. */
  readonly status: string;
  /** GREEN/YELLOW/RED, ou null enquanto nao ha volume para medir. */
  readonly quality: string | null;
  readonly body: string | null;
  /**
   * Os exemplos que foram enviados junto na criacao, quando ha variaveis.
   *
   * Servem para a previa mostrar uma mensagem de verdade em vez de `{{1}}` — e
   * sao tambem o que a Meta de fato leu ao revisar.
   */
  readonly examples: readonly string[];
  /** Preenchido quando `status` e REJECTED. */
  readonly rejectedReason: string | null;
}

/** O que a tela manda ao criar um template. */
export interface CreateTemplateRequest {
  readonly name: string;
  readonly body: string;
  readonly footer?: string;
  readonly language: string;
  readonly category: string;
  /** Um exemplo por variavel, na ordem de `{{1}}`, `{{2}}`… */
  readonly examples: readonly string[];
}

/** O que a tela de templates recebe: o estado do canal + a lista. */
export interface ClientTemplates {
  readonly clientId: Uuid;
  /** `null` quando o cliente ainda nao tem canal conectado. */
  readonly wabaId: string | null;
  readonly templates: readonly MessageTemplate[];
  /** Motivo de a lista estar vazia sem ser "nao ha templates". */
  readonly unavailable: string | null;
}

export type ReviewSendMode = 'text' | 'template';

export interface ReviewSendRequest {
  readonly to: string;
  readonly mode: ReviewSendMode;
  readonly body?: string;
  readonly templateName?: string;
  readonly language?: string;
  readonly bodyParams?: readonly string[];
}

export interface ReviewSendResult {
  readonly wamid: string;
  readonly to: string;
  readonly mode: ReviewSendMode;
  readonly preview: string;
}

export interface ReviewTemplateRequest {
  readonly name: string;
  readonly bodyText: string;
  readonly language?: string;
}

export interface ReviewTemplateResult {
  readonly id: string;
  readonly status: string;
  readonly category: string;
}

export interface LoginRequest {
  readonly email: string;
  readonly password: string;
}

export interface LoginResponse {
  readonly token: string;
  readonly me: Me;
}

// ── Médicos e canais ────────────────────────────────────────────────────────

export interface Doctor {
  readonly id: Uuid;
  /**
   * De qual cliente ele é. Nulo só em médico órfão, que a lista de clientes não
   * alcança.
   *
   * Sem este campo não havia caminho de um cliente para os `doctorId` dele na
   * lista: `ClientDetail.doctor` devolve só o primeiro, e numa clínica com dois
   * médicos o segundo simplesmente não existia para a tela.
   */
  readonly clientId: Uuid | null;
  readonly fullName: string;
  readonly specialty: string | null;
  readonly color: string;
  readonly timezone: string;
}

export type ChannelState =
  | 'draft'
  | 'verifying'
  | 'active'
  | 'degraded'
  | 'inactive_warning'
  | 'disconnected'
  | 'suspended'
  | 'offboarded';

export type QualityRating = 'GREEN' | 'YELLOW' | 'RED' | 'UNKNOWN';
export type BillingStatus = 'unknown' | 'missing_payment_method' | 'ok' | 'blocked';

export interface Channel {
  readonly id: Uuid;
  readonly doctorId: Uuid;
  readonly doctorName: string;
  readonly displayPhoneNumber: string;
  readonly displayName: string;
  readonly isCoexistence: boolean;
  readonly provider: WhatsappProvider;
  readonly state: ChannelState;
  readonly qualityRating: QualityRating;
  readonly billingStatus: BillingStatus;
  /** Mensagens por dia. `null` significa sem teto, e não "desconhecido". */
  readonly messagingTier: number | null;
  /** Dias desde o último sinal do app do médico. `null` para canal clássico. */
  readonly daysSinceLastEcho: number | null;
  /**
   * Quando qualidade e limite foram conferidos na Meta. `null` = nunca, e nesse
   * caso a tela não deve afirmar esses valores.
   */
  readonly syncedAt: Iso | null;
}

// ── Inbox ───────────────────────────────────────────────────────────────────

export type ConversationStatus = 'open' | 'pending' | 'answered_by_doctor' | 'resolved';

/**
 * Estado da janela de 24 h, computado no servidor.
 *
 * A UI **não** reimplementa a regra: ela chama e desabilita o composer com o motivo.
 * Reimplementar no cliente é como se manda free-form fora da janela e se toma `131047`.
 */
export interface ServiceWindow {
  readonly open: boolean;
  readonly expiresAt: Iso | null;
  readonly minutesRemaining: number | null;
  /** Free entry point: 72 h e TUDO é gratuito nela. Semântica diferente da de 24 h. */
  readonly freeEntryPoint: boolean;
}

export interface ConversationSummary {
  readonly id: Uuid;
  /** Se a conversa é de um médico da minha operação. Fora dela, veio por uma equipe. */
  readonly mine: boolean;
  /** Quem trouxe o médico, para o selo "da Ana". `null` na casa. */
  readonly ownerName: string | null;
  /** Quem é a atendente da conversa, para "minhas" e "sem dono". */
  readonly assigneeId: Uuid | null;
  /** O paciente. E por ele que a consulta e marcada. */
  readonly contactId: Uuid;
  readonly contactName: string;
  readonly waId: string;
  readonly phone: string | null;
  /**
   * De QUAL cliente esta pessoa vem. Num inbox que junta os pacientes de todos os
   * médicos, isto não é metadado — é a informação que impede a atendente de responder
   * em nome do médico errado.
   */
  readonly clientId: Uuid;
  readonly clientName: string;
  readonly clientColor: string;
  readonly doctorId: Uuid;
  readonly doctorName: string;
  readonly doctorColor: string;
  readonly status: ConversationStatus;
  readonly assigneeName: string | null;
  readonly lastMessageAt: Iso | null;
  readonly lastMessagePreview: string | null;
  readonly unreadCount: number;
  readonly awaitingReply: boolean;
  /**
   * Minutos desde a última mensagem do paciente, quando ela ainda não foi respondida.
   *
   * Computado no SERVIDOR e não na tela: sem isso, uma pessoa esperando cinco minutos
   * e outra esperando três horas são a mesma linha na lista — e a de três horas é a
   * que vira reclamação com o médico.
   */
  readonly minutesWaiting: number | null;
  readonly tags: readonly ConversationTag[];
  readonly serviceWindow: ServiceWindow;
  /** Próxima consulta do paciente, se houver — é o contexto que a secretária precisa. */
  readonly nextAppointmentAt: Iso | null;
  readonly autopilot: ConversationAutopilot;
}

export type MessageDirection = 'in' | 'out';
export type MessageOrigin = 'cloud_api' | 'smb_echo' | 'history' | 'inbound';
export type DeliveryState =
  | 'none'
  | 'queued'
  | 'in_doubt'
  | 'accepted'
  | 'held'
  | 'sent'
  | 'failed'
  | 'delivered'
  | 'read'
  | 'played';

/**
 * O anexo de uma mensagem.
 *
 * ── Não há URL aqui, e é de propósito ──
 * A Cloud API não entrega a mídia: ela dá um id, e a URL de download **expira
 * em 5 minutos** ✅. Uma URL no contrato estaria vencida antes de a tela
 * renderizar. Quem serve os bytes é `GET /api/messages/{id}/media`, que pede a
 * URL à Meta no instante do pedido.
 *
 * ── E não há cópia nossa ──
 * O arquivo nunca é gravado no Zenda. Quem quiser guardar para sempre baixa
 * para o computador — é por isso que `expiresAt` existe e aparece na tela.
 */
export interface MessageMedia {
  readonly mime: string | null;
  readonly filename: string | null;
  /** Só no envio: o webhook de entrada não informa tamanho ✅. */
  readonly sizeBytes: number | null;
  /**
   * Quando o anexo deixa de ser recuperável.
   *
   * Sete dias para o que o paciente mandou, trinta para o que saiu daqui — o id
   * de webhook de entrada *"expire after 7 days"*, o de upload dura 30 ✅. A
   * assimetria é da plataforma, e é justamente o que a atendente precisa saber.
   */
  readonly expiresAt: Iso;
}

export interface Message {
  readonly id: Uuid;
  readonly occurredAt: Iso;
  readonly direction: MessageDirection;
  readonly origin: MessageOrigin;
  readonly waType: string;
  readonly body: string | null;
  readonly media: MessageMedia | null;
  readonly deliveryState: DeliveryState;
  readonly failureCode: number | null;
  readonly isInternalNote: boolean;
  readonly authorName: string | null;
  /** Estimativa informativa (ADR 0030). `null` = sem custo registrado. */
  readonly cost: MessageCost | null;
  /** Saiu pelo piloto automático: o selo "piloto", visível só aqui. */
  readonly byAutopilot: boolean;
}

/** Valores em micro-reais (1 real = 1.000.000), inteiros para a soma fechar. */
export interface MessageCost {
  readonly totalMicros: number;
  readonly metaMicros: number;
  /** A taxa do parceiro que leva a mensagem (Gupshup, ADR 0029). */
  readonly partnerMicros: number;
  /** O `pricing.category` da Meta: service, utility, marketing, authentication. */
  readonly category: string | null;
  readonly chargedByMeta: boolean;
}

/** O gasto estimado com mensagens no mês, da operação de quem está logado (ADR 0030). */
export interface MonthlyMessageCost {
  /** `AAAA-MM`, no fuso de São Paulo. */
  readonly month: string;
  readonly currency: 'BRL';
  readonly messages: number;
  readonly totalMicros: number;
  readonly metaMicros: number;
  readonly partnerMicros: number;
  /** O que o parceiro já debitou neste mês, pelo extrato dele. `null` = sem canal nele. */
  readonly billed: PartnerBilledCost | null;
}

/** O extrato real do parceiro, na moeda da carteira (USD na Gupshup), em micro-unidades. */
export interface PartnerBilledCost {
  readonly partner: 'gupshup';
  readonly currency: string;
  readonly totalMicros: number;
  readonly metaMicros: number;
  readonly partnerMicros: number;
  readonly discountMicros: number;
  readonly messages: number;
  /** Último dia (UTC) já consolidado no extrato. `null` = nada consolidado no mês. */
  readonly throughDay: string | null;
}

export interface ConversationDetail {
  readonly conversation: ConversationSummary;
  readonly messages: readonly Message[];
  /**
   * Valores das variaveis de resposta pronta para ESTE paciente.
   *
   * Vem do servidor, e nao montado na tela, porque metade deles nao esta no
   * `ConversationSummary` — `retorno` e `ultima_consulta` moram na ficha, que a
   * caixa de entrada nao carrega. `null` significa "existe a variavel, mas este
   * paciente nao tem o dado", que e exatamente o caso que bloqueia o envio.
   */
  readonly variaveis: Readonly<Record<string, string | null>>;
  /** Ninguém escreveu ainda e a ficha não registra autorização: o template não sai. */
  readonly precisaAutorizacao: boolean;
  readonly autopilotDetail: ConversationAutopilotDetail;
}

export interface SendMessageRequest {
  readonly body: string;
  readonly isInternalNote?: boolean;
}

/**
 * Reabrir a conversa com um template aprovado.
 *
 * É o único caminho que atravessa a janela de 24 h fechada ✅. Os parâmetros vão
 * na ordem das variáveis do template — `{{1}}` é o primeiro.
 */
export interface SendTemplateRequest {
  readonly templateName: string;
  readonly language: string;
  readonly bodyParams?: readonly string[];
}

// ── Agenda ──────────────────────────────────────────────────────────────────

export type AppointmentStatus =
  'requested' | 'scheduled' | 'confirmed' | 'checked_in' | 'completed' | 'cancelled' | 'no_show';

export interface Appointment {
  readonly id: Uuid;
  readonly doctorId: Uuid;
  readonly doctorName: string;
  readonly doctorColor: string;
  /** O dono do WhatsApp, que recebe as mensagens. */
  readonly patientName: string;
  /** Quem é atendido, quando não é o próprio paciente (ADR 0028). */
  readonly dependentName: string | null;
  readonly startsAt: Iso;
  readonly endsAt: Iso;
  readonly status: AppointmentStatus;
  readonly modality: 'in_person' | 'telehealth';
  readonly title: string | null;
  readonly conversationId: Uuid | null;
}

// ── Painel ──────────────────────────────────────────────────────────────────

/**
 * Os números do painel — e **só** os que o painel mostra.
 *
 * Cada campo aqui é uma subquery que roda a cada duas horas de aba aberta, por
 * atendente. Campo que nenhuma tela lê é custo puro no caminho mais quente do
 * produto, e some sem ninguém notar até virar lentidão sem dono: `appointmentsToday`,
 * `confirmedToday`, `channelsHealthy`, `channelsTotal`, `uniqueRecipients24h` e
 * `messagingTier` saíram por isso — restos da tela de canais, que virou atributo do
 * cliente. Medido: 7,9 ms para 0,6 ms na base da demo, e os dois `count` em
 * `appointments` eram a maior parte.
 *
 * Antes de acrescentar um campo aqui: existe tile para ele?
 */
export interface DashboardStats {
  readonly conversationsOpen: number;
  readonly awaitingReply: number;
  /**
   * Conversas **com resposta pendente** cuja janela de 24 h expira em menos de 2 h.
   *
   * A pendência faz parte da conta: sem ela o tile contava também a conversa que a
   * atendente já respondeu, onde a janela fechar não custa nada — e o painel dizia
   * "precisa da sua atenção" sobre trabalho que já estava feito, discordando da lista
   * de aguardando que fica logo abaixo dele.
   */
  readonly windowsClosingSoon: number;
  /** A operação da atendente, que é o que ela precisa saber sobre o próprio negócio. */
  readonly clientsActive: number;
  /** Canais que precisam de ação: desconectado, sem cartão, ou qualidade em queda. */
  readonly clientsNeedingAttention: number;
  readonly monthlyRevenueBrl: number;
  readonly paymentsOverdue: number;
}

// ── Erros ───────────────────────────────────────────────────────────────────

export interface ApiError {
  readonly code: string;
  readonly message: string;
}

export const API_ROUTES = {
  login: '/api/auth/login',
  me: '/api/me',
  clients: '/api/clients',
  client: (id: Uuid) => `/api/clients/${id}`,
  markPaymentPaid: (clientId: Uuid, month: string) =>
    `/api/clients/${clientId}/payments/${month}/paid`,
  markPaymentUnpaid: (clientId: Uuid, month: string) =>
    `/api/clients/${clientId}/payments/${month}/unpaid`,
  connectChannel: (doctorId: Uuid) => `/api/doctors/${doctorId}/connect`,
  reconnectChannel: (channelId: Uuid) => `/api/channels/${channelId}/reconnect`,
  agendaConfig: (clientId: Uuid) => `/api/clients/${clientId}/agenda`,
  agendaSettings: (clientId: Uuid) => `/api/clients/${clientId}/agenda/settings`,
  locations: (clientId: Uuid) => `/api/clients/${clientId}/locations`,
  location: (clientId: Uuid, id: Uuid) => `/api/clients/${clientId}/locations/${id}`,
  rules: (clientId: Uuid) => `/api/clients/${clientId}/availability`,
  rulesBulk: (clientId: Uuid) => `/api/clients/${clientId}/availability/bulk`,
  rule: (clientId: Uuid, id: Uuid) => `/api/clients/${clientId}/availability/${id}`,
  exceptions: (clientId: Uuid) => `/api/clients/${clientId}/availability/exceptions`,
  agendaTypes: (clientId: Uuid) => `/api/clients/${clientId}/agenda/types`,
  agendaType: (clientId: Uuid, id: Uuid) => `/api/clients/${clientId}/agenda/types/${id}`,
  exception: (clientId: Uuid, id: Uuid) => `/api/clients/${clientId}/availability/exceptions/${id}`,
  availabilityDays: (clientId: Uuid, from: string, to: string) =>
    `/api/clients/${clientId}/availability/days?from=${from}&to=${to}`,
  /**
   * A agenda de UM médico, dia por dia. `doctorId` é obrigatório de propósito: a
   * agenda pertence a um médico, e um cliente pode ser uma clínica com vários
   * (ADR 0010). Servir a soma deles seria inventar uma agenda que ninguém tem.
   */
  agendaDays: (clientId: Uuid, doctorId: Uuid, from: string, to: string) =>
    `/api/clients/${clientId}/agenda/days?doctorId=${doctorId}&from=${from}&to=${to}`,

  agendaIntegration: (clientId: Uuid) => `/api/clients/${clientId}/agenda/integration`,
  /**
   * Os calendários que a conta alcança na plataforma, para o mapeamento.
   *
   * `POST` numa leitura porque a credencial vai no CORPO: em query string ela cairia
   * em log de acesso, referer e histórico do navegador.
   */
  platformCalendars: (clientId: Uuid) => `/api/clients/${clientId}/agenda/platform-calendars`,
  calendarLinks: (clientId: Uuid) => `/api/clients/${clientId}/agenda/links`,
  calendarLink: (clientId: Uuid, id: Uuid) => `/api/clients/${clientId}/agenda/links/${id}`,
  /**
   * Republica a disponibilidade agora. **Não tem botão na tela**, de propósito.
   *
   * A sincronia é tempo real: a grade republica no rastro de quem a mudou, a
   * marcação nasce na plataforma, e o que a paciente marca lá chega por push. Um
   * botão "publicar agora" convida a acreditar que sem ele nada acontece — e o dia
   * em que alguém precisar clicar, o que está quebrado é a sincronia, não a
   * paciência de quem clica.
   *
   * A rota fica porque é a superfície que o smoke exercita, e porque um operador
   * precisa de um jeito de forçar uma passada sem esperar o ciclo.
   */
  publishCalendar: (clientId: Uuid, id: Uuid) =>
    `/api/clients/${clientId}/agenda/links/${id}/publish`,
  /** Idem: busca na plataforma o que a paciente marcou por lá. Sem botão. */
  pullCalendar: (clientId: Uuid, id: Uuid) => `/api/clients/${clientId}/agenda/links/${id}/pull`,
  /**
   * Instrumento: a paciente reservando do lado de lá.
   *
   * É o único caminho que não se consegue provocar de fora em modo `simulated`, e
   * sem ele metade da integração — a que vem para cá — ficaria sem prova.
   */
  simulateBooking: (clientId: Uuid, id: Uuid) =>
    `/api/clients/${clientId}/agenda/links/${id}/simulate-booking`,
  resolveConflict: (clientId: Uuid, id: Uuid) =>
    `/api/clients/${clientId}/agenda/conflicts/${id}/resolve`,
  freeSlots: (clientId: Uuid) => `/api/clients/${clientId}/availability/search`,
  checkSlots: (clientId: Uuid) => `/api/clients/${clientId}/availability/check`,
  book: `/api/appointments`,
  patientAgenda: (contactId: Uuid) => `/api/contacts/${contactId}/agenda`,
  cancelPatientAgenda: (contactId: Uuid, clientId?: Uuid) =>
    `/api/contacts/${contactId}/agenda${clientId === undefined ? '' : `?clientId=${clientId}`}`,
  cancelAppointment: (id: Uuid) => `/api/appointments/${id}`,
  rescheduleAppointment: (id: Uuid) => `/api/appointments/${id}/reschedule`,
  confirmAppointment: (id: Uuid) => `/api/appointments/${id}/confirm`,
  appointmentPresence: (id: Uuid) => `/api/appointments/${id}/presence`,
  templateModels: `/api/template-models`,
  templateModel: (id: Uuid) => `/api/template-models/${id}`,
  quickReplies: `/api/quick-replies`,
  quickReply: (id: Uuid) => `/api/quick-replies/${id}`,
  syncChannel: (channelId: Uuid) => `/api/channels/${channelId}/sync`,
  disconnectChannel: (channelId: Uuid) => `/api/channels/${channelId}/disconnect`,
  doctors: '/api/doctors',
  channels: '/api/channels',
  conversations: '/api/conversations',
  conversation: (id: Uuid) => `/api/conversations/${id}`,
  sendMessage: (id: Uuid) => `/api/conversations/${id}/messages`,
  sendMedia: (id: Uuid) => `/api/conversations/${id}/media`,
  /** Serve os bytes do anexo. `baixar` troca exibir por salvar no computador. */
  messageMedia: (id: Uuid, baixar = false) =>
    `/api/messages/${id}/media${baixar ? '?download=1' : ''}`,
  stream: '/api/stream',
  read: (id: Uuid) => `/api/conversations/${id}/read`,
  reopen: (id: Uuid) => `/api/conversations/${id}/reopen`,
  resolve: (id: Uuid) => `/api/conversations/${id}/resolve`,
  appointments: '/api/appointments',
  stats: '/api/dashboard/stats',
  costsMonth: '/api/costs/month',

  perfil: '/api/me/perfil',
  focus: '/api/me/focus',
  plantao: '/api/me/plantao',
  typing: (id: Uuid) => `/api/conversations/${id}/typing`,
  teams: '/api/teams',
  team: (id: Uuid) => `/api/teams/${id}`,
  teamArchive: (id: Uuid) => `/api/teams/${id}/archive`,
  teamLeave: (id: Uuid) => `/api/teams/${id}/leave`,
  teamTransfer: (id: Uuid) => `/api/teams/${id}/transfer`,
  teamInvites: (id: Uuid) => `/api/teams/${id}/invites`,
  teamInvite: (id: Uuid, inviteId: Uuid) => `/api/teams/${id}/invites/${inviteId}`,
  teamMember: (id: Uuid, userId: Uuid) => `/api/teams/${id}/members/${userId}`,
  teamShares: (id: Uuid) => `/api/teams/${id}/shares`,
  teamShare: (id: Uuid, clientId: Uuid) => `/api/teams/${id}/shares/${clientId}`,
  teamSharePolicy: (id: Uuid) => `/api/teams/${id}/share-policy`,
  teamShifts: (id: Uuid) => `/api/teams/${id}/shifts`,
  teamShift: (id: Uuid, shiftId: Uuid) => `/api/teams/${id}/shifts/${shiftId}`,
  teamShiftOverrides: (id: Uuid) => `/api/teams/${id}/shift-overrides`,
  teamShiftOverride: (id: Uuid, overrideId: Uuid) =>
    `/api/teams/${id}/shift-overrides/${overrideId}`,
  teamAutoAssign: (id: Uuid) => `/api/teams/${id}/auto-assign`,
  teamActivity: (id: Uuid) => `/api/teams/${id}/activity`,
  clientActivity: (id: Uuid) => `/api/clients/${id}/activity`,
  assignConversation: (id: Uuid) => `/api/conversations/${id}/assign`,
  teamTerm: '/api/teams/term',
  attestationText: '/api/teams/attestation',
  myInvites: '/api/teams/invites',
  acceptInvite: (inviteId: Uuid) => `/api/teams/invites/${inviteId}/accept`,
  declineInvite: (inviteId: Uuid) => `/api/teams/invites/${inviteId}/decline`,

  patients: '/api/patients',
  contactSearch: '/api/contacts/search',
  clientPatients: (clientId: Uuid) => `/api/clients/${clientId}/patients`,
  patient: (clientId: Uuid, contactId: Uuid) => `/api/clients/${clientId}/patients/${contactId}`,
  patientDependents: (clientId: Uuid, contactId: Uuid) =>
    `/api/clients/${clientId}/patients/${contactId}/dependents`,
  patientDependent: (clientId: Uuid, contactId: Uuid, dependentId: Uuid) =>
    `/api/clients/${clientId}/patients/${contactId}/dependents/${dependentId}`,
  patientConversation: (clientId: Uuid, contactId: Uuid) =>
    `/api/clients/${clientId}/patients/${contactId}/conversation`,
  patientTemplates: (clientId: Uuid, contactId: Uuid) =>
    `/api/clients/${clientId}/patients/${contactId}/templates`,

  reviewStatus: '/api/review/status',
  reviewSend: '/api/review/send',
  reviewTemplate: '/api/review/template',
  metaConfig: '/api/meta/config',
  metaOnboard: '/api/meta/onboard',
  metaSessions: '/api/meta/sessions',
  connectLink: (doctorId: Uuid) => `/api/doctors/${doctorId}/connect-link`,
  connectLinkCheck: (doctorId: Uuid) => `/api/doctors/${doctorId}/connect-link/check`,
  conviteGupshupLink: (token: string) => `/api/convite/${encodeURIComponent(token)}/gupshup-link`,
  convite: (token: string) => `/api/convite/${encodeURIComponent(token)}`,
  conviteMetaConfig: (token: string) => `/api/convite/${encodeURIComponent(token)}/meta-config`,
  conviteSessions: (token: string) => `/api/convite/${encodeURIComponent(token)}/sessions`,
  conviteOnboard: (token: string) => `/api/convite/${encodeURIComponent(token)}/onboard`,

  tags: '/api/tags',
  tag: (id: Uuid) => `/api/tags/${id}`,
  clientTemplates: (clientId: Uuid) => `/api/clients/${clientId}/templates`,
  conversationTags: (id: Uuid) => `/api/conversations/${id}/tags`,
  conversationTemplate: (id: Uuid) => `/api/conversations/${id}/templates`,
  conversationTag: (id: Uuid, tagId: Uuid) => `/api/conversations/${id}/tags/${tagId}`,
} as const;

// ── Biblioteca e respostas prontas ──────────────────────────────────────────

/**
 * Rascunho de template guardado no Zenda.
 *
 * NAO existe na Meta. Serve para que a aba de templates de um medico ja mostre
 * os modelos prontos para submeter, em vez de a atendente reescrever "lembrete
 * - vespera" a cada medico novo.
 */
export interface TemplateModel {
  readonly id: Uuid;
  readonly name: string;
  readonly body: string;
  readonly footer: string | null;
  readonly language: string;
  readonly category: string;
  /** Um exemplo por variavel, na ordem de `{{1}}`, `{{2}}`… */
  readonly examples: readonly string[];
}

export interface TemplateModelUpsert {
  readonly name: string;
  readonly body: string;
  readonly footer?: string | null;
  readonly language?: string;
  readonly category?: string;
  readonly examples?: readonly string[];
}

/**
 * Resposta pronta para usar dentro da conversa. Nunca passa pela Meta — e texto
 * enviado na janela de 24 h.
 *
 * `clientId` nulo vale para todos os medicos. Preenchido, so aparece nas
 * conversas daquele medico: e a diferenca entre "Bom dia, tudo bem?" e "A Dra.
 * Helena atende as sextas a partir das 9h".
 */
export interface QuickReply {
  readonly id: Uuid;
  readonly clientId: Uuid | null;
  readonly clientName: string | null;
  readonly title: string;
  readonly body: string;
  /**
   * Se quem pergunta pode editar. As do cliente compartilhado vêm junto para serem
   * usadas na conversa (docs/domain/equipes.md §3.2); editar pede casa ou gestão.
   */
  readonly editable: boolean;
}

export interface QuickReplyUpsert {
  readonly title: string;
  readonly body: string;
  readonly clientId?: Uuid | null;
}

// ── Agenda: locais e disponibilidade ────────────────────────────────────────

export type Modality = 'in_person' | 'telehealth';

export interface Location {
  readonly id: Uuid;
  readonly name: string;
  readonly address: string | null;
  readonly city: string | null;
  /** Nulo herda o fuso do médico. */
  readonly timezone: string | null;
  readonly color: string;
  readonly active: boolean;
}

export interface LocationUpsert {
  readonly name: string;
  readonly address?: string | null;
  readonly city?: string | null;
  readonly timezone?: string | null;
  readonly color?: string;
  readonly active?: boolean;
}

/**
 * O padrão semanal. `locationId` nulo é teleconsulta, que não acontece em lugar
 * nenhum; `validUntil` nulo é sem fim.
 */
export interface AvailabilityRule {
  readonly id: Uuid;
  readonly locationId: Uuid | null;
  readonly locationName: string | null;
  /** 0 = domingo. */
  readonly weekday: number;
  /** 'HH:MM'. */
  readonly startsAt: string;
  readonly endsAt: string;
  readonly modality: Modality;
  /** Vazio = todas as semanas do mês. */
  readonly weeksOfMonth: readonly number[];
  readonly validFrom: string;
  readonly validUntil: string | null;
  readonly active: boolean;
}

export interface AvailabilityRuleUpsert {
  readonly locationId?: Uuid | null;
  readonly weekday: number;
  readonly startsAt: string;
  readonly endsAt: string;
  readonly modality?: Modality;
  readonly weeksOfMonth?: readonly number[];
  readonly validFrom?: string;
  readonly validUntil?: string | null;
  readonly active?: boolean;
}

/**
 * Criação em lote: os mesmos horários em vários dias da semana viram N regras.
 * É o caminho normal — ninguém configura "segunda a sexta" uma linha por vez.
 */
export interface AvailabilityBulkRequest {
  readonly locationId?: Uuid | null;
  /**
   * A grade inteira daquele local, de uma vez.
   *
   * Uma chamada por faixa não funciona: com `replace`, cada chamada limparia o
   * que a anterior acabou de criar; sem ele, salvar duas vezes duplicaria tudo.
   * A grade é um estado, e estado se salva inteiro.
   */
  readonly ranges: readonly {
    readonly weekday: number;
    readonly startsAt: string;
    readonly endsAt: string;
  }[];
  readonly modality?: Modality;
  readonly weeksOfMonth?: readonly number[];
  readonly validFrom?: string;
  readonly validUntil?: string | null;
  /** Desativa TODA a disponibilidade daquele local antes de criar. */
  readonly replace?: boolean;
}

export interface AvailabilityException {
  readonly id: Uuid;
  readonly kind: 'block' | 'extra';
  readonly onDate: string;
  readonly startsAt: string | null;
  readonly endsAt: string | null;
  readonly locationId: Uuid | null;
  readonly locationName: string | null;
  readonly modality: Modality | null;
  readonly reason: string | null;
  /**
   * De qual plataforma este bloqueio veio, quando ele é o reflexo de um bloqueio que o
   * médico pôs na agenda de lá. Esses a atendente não edita nem apaga daqui: a verdade é lá.
   */
  readonly mirroredFrom: string | null;
}

export interface AvailabilityExceptionUpsert {
  readonly kind: 'block' | 'extra';
  readonly onDate: string;
  /** Período: cria uma exceção por dia entre `onDate` e `untilDate`. Férias. */
  readonly untilDate?: string | null;
  readonly startsAt?: string | null;
  readonly endsAt?: string | null;
  readonly locationId?: Uuid | null;
  readonly modality?: Modality | null;
  readonly reason?: string | null;
}

export interface AgendaSettings {
  readonly minNoticeMinutes: number;
  readonly maxHorizonDays: number;
  readonly travelBufferMin: number;
}

/** Tudo o que a tela de disponibilidade precisa, numa chamada. */
export interface AgendaConfig {
  readonly locations: readonly Location[];
  readonly rules: readonly AvailabilityRule[];
  readonly exceptions: readonly AvailabilityException[];
  readonly settings: AgendaSettings;
  /**
   * Os tipos de consulta daquele médico, com a duração de cada um.
   *
   * Vêm junto porque a marcação feita na própria agenda precisa deles para
   * preencher a duração, e antes ela não tinha de onde tirar uma — caía num
   * padrão do cliente que ninguém via. Ver ADR 0020.
   */
  readonly types: readonly AppointmentType[];
  /**
   * Quanto dura a maioria das consultas daquele médico, **observado** e não
   * configurado: a duração mais frequente dos últimos 90 dias, com o tipo mais curto
   * como piso.
   *
   * É o que um clique na agenda cria. Sai de observação em vez de cadastro porque a
   * duração deixou de ser configuração (ADR 0020), e um padrão configurável seria o
   * quarto dono do mesmo número. Acerta sozinho quando o médico muda de hábito.
   */
  readonly usualDurationMin: number;
}

/**
 * Uma janela de atendimento de um dia: o médico atende deste horário até aquele.
 *
 * É a unidade da disponibilidade. Não há grade de horários: a duração da consulta
 * é escolhida na marcação, e a janela não sabe qual vai ser. Ver ADR 0020.
 */
export interface AgendaWindow {
  /** 'HH:MM'. */
  readonly de: string;
  readonly ate: string;
  /** Minutos desde a meia-noite, para posicionar na linha do tempo. */
  readonly minutos: number;
  readonly duracaoTotal: number;
  readonly locationId: Uuid | null;
  readonly locationName: string | null;
  readonly modality: Modality;
  /**
   * Aberta por exceção, e não pela rotina — o sábado de mutirão, a tarde
   * esticada. Apresentá-la como rotina esconderia a decisão que a criou.
   */
  readonly extra: boolean;
  /** Consultas que começam dentro dela. */
  readonly ocupados: number;
  /**
   * Minutos da janela sem consulta nenhuma.
   *
   * Substitui a contagem de horários livres, que passou a mentir: uma janela de
   * 08:00 a 12:00 com oito consultas de 20 min diria "0 livres" com três horas
   * vagas.
   */
  readonly livresMin: number;
}

/** Um dia resolvido: as janelas em que aquele médico atende. */
export interface AvailabilityDay {
  readonly date: string;
  readonly windows: readonly AgendaWindow[];
}

// ── A agenda de um médico, dia por dia ──────────────────────────────────────

/**
 * De onde a consulta veio.
 *
 * Hoje é sempre `zenda`, porque a Zenda é o único caminho de escrita. O campo
 * existe na resposta desde já para que a tela não precise ser reescrita quando
 * deixar de ser: uma agenda que mostra compromisso sem dizer a procedência
 * passa a mentir no dia em que houver duas procedências.
 */
export type AppointmentSource = 'zenda' | 'google_calendar' | 'clinic_system' | 'import';

/**
 * Uma consulta como o dia da agenda a mostra.
 *
 * Diferente de `Appointment`, que fala em instante UTC: aqui a hora é **local**,
 * no fuso gravado na consulta, porque é a hora que o consultório vive e a única
 * que cruza com a disponibilidade sem conversão no meio.
 */
export interface AgendaAppointment {
  readonly id: Uuid;
  readonly doctorId: Uuid;
  readonly doctorName: string;
  readonly doctorColor: string;
  readonly contactId: Uuid;
  readonly patientName: string;
  /** Quem é atendido, quando não é o próprio paciente (ADR 0028). */
  readonly dependentName: string | null;
  /** 'HH:MM' no fuso da consulta. */
  readonly hora: string;
  /** Minutos desde a meia-noite local. É o que posiciona o bloco na régua. */
  readonly minutos: number;
  readonly duracao: number;
  readonly locationId: Uuid | null;
  readonly locationName: string | null;
  readonly modality: Modality;
  readonly status: AppointmentStatus;
  /**
   * O tema, escrito à mão pela atendente.
   *
   * DADO DE SAÚDE: é interno. Nunca entra em log, trace, evento de domínio nem
   * em mensagem enviada à paciente.
   */
  readonly title: string | null;
  readonly seriesId: Uuid | null;
  readonly overbooked: boolean;
  readonly conversationId: Uuid | null;
  /** Quem marcou e quem mexeu por último (docs/domain/equipes.md §6). `null` em consulta antiga ou de fora. */
  readonly createdByName: string | null;
  readonly updatedByName: string | null;
  readonly source: AppointmentSource;
  /**
   * Os campos do fornecedor com que esta consulta entrou, por nome.
   *
   * Somente identificadores opacos — rótulo de serviço pode ser dado de saúde, id
   * não é. A tela traduz para rótulo usando o retrato do vínculo.
   */
  readonly externalFields: Readonly<Record<string, string>> | null;
}

/**
 * Por que o dia não tem horário nenhum.
 *
 * `sem_rotina` e `bloqueado` chegavam indistinguíveis em `AvailabilityDay`, que
 * devolve `slots: []` nos dois casos e não devolve exceção alguma. A diferença é
 * o que a tela precisa dizer: "o médico não atende nesse dia" convida a abrir um
 * extra, "férias" convida a remover o bloqueio, e confundir os dois manda a
 * atendente para o lugar errado.
 */
export type DayClosedReason = 'sem_rotina' | 'bloqueado';

/** Um dia da agenda de um médico: o que a rotina abriu, o que a quebrou, e quem está marcado. */
export interface AgendaDay {
  readonly date: string;
  /** 0 = domingo. */
  readonly weekday: number;
  /** Nulo quando o dia tem janela. Preenchido diz por que não tem. */
  readonly closedReason: DayClosedReason | null;
  readonly windows: readonly AgendaWindow[];
  readonly appointments: readonly AgendaAppointment[];
  /** As exceções daquela data, para a tela dizer o motivo em vez de só o efeito. */
  readonly exceptions: readonly AvailabilityException[];
}

// ── Integração com plataforma de terceiro ───────────────────────────────────

/**
 * O vínculo de um par (médico, local) com o calendário dele numa plataforma.
 *
 * É por par porque do outro lado **o calendário é o endereço** — na Docplanner ele
 * não tem id próprio, é o `address_id`. Ver [ADR 0016](../../../docs/adr/0016-a-agenda-como-interface-de-plataforma-de-terceiro.md).
 */
export interface CalendarLink {
  readonly id: Uuid;
  readonly doctorId: Uuid;
  readonly doctorName: string;
  /** Nulo no calendário de teleconsulta, que lá é um endereço só-online ⚠️. */
  readonly locationId: Uuid | null;
  readonly locationName: string | null;
  readonly modality: Modality;
  readonly platform: 'doctoralia';
  /**
   * `simulated` não fala com ninguém e existe porque o acesso à API é por
   * parceria aprovada, cliente por cliente: um caminho que só funciona com
   * credencial é um caminho que ninguém consegue exercitar.
   */
  readonly mode: 'simulated' | 'live';
  readonly externalAddressId: string | null;
  /**
   * @deprecated Substituído por `bookingFieldDefaults`. Continua sendo lido como
   * fallback de vínculo criado antes da migração 0035.
   */
  readonly externalServiceId: string | null;
  /**
   * O retrato das opções que a plataforma ofereceu quando alguém configurou.
   *
   * A caixa de marcação lê **daqui**, e não da plataforma: quem marca está ao
   * telefone, e uma chamada de rede nesse caminho transforma instabilidade lá em
   * "não consigo marcar" aqui.
   */
  readonly bookingFieldOptions: Readonly<Record<string, readonly BookingFieldOption[]>>;
  /** O valor pré-selecionado de cada campo. É o que o caminho automático usa. */
  readonly bookingFieldDefaults: Readonly<Record<string, string>>;
  readonly active: boolean;
  /** A grade mudou e a plataforma ainda não sabe. */
  readonly fullResyncNeeded: boolean;
  readonly lastPublishedAt: Iso | null;
  readonly lastPulledAt: Iso | null;
  /** O que impediu a última publicação. Fica na linha porque log não chega na atendente. */
  readonly lastError: string | null;
  /**
   * A URL que o cliente cola no painel da plataforma para as reservas chegarem na
   * hora, em vez de esperar o próximo ciclo.
   *
   * Ela **carrega um segredo**: é ele que identifica o calendário e autentica a
   * notificação, que não vem assinada. Tratar como credencial — e é por isso que o
   * contrato expõe a URL montada, e não o segredo cru.
   */
  readonly pushUrl: string | null;
}

export interface CalendarLinkUpsert {
  readonly doctorId: Uuid;
  /** Nulo, com `modality: 'telehealth'`, vincula o calendário online do médico. */
  readonly locationId: Uuid | null;
  readonly modality?: Modality;
  /** Qual fornecedor. Ausente = o primeiro do registro, que hoje é o único. */
  readonly platform?: AgendaPlatformId;
  readonly mode?: 'simulated' | 'live';
  readonly externalFacilityId?: string | null;
  readonly externalDoctorId?: string | null;
  readonly externalAddressId?: string | null;
  /**
   * @deprecated Use `bookingFieldDefaults`. Aceito por compatibilidade.
   */
  readonly externalServiceId?: string | null;
  /** O valor pré-selecionado de cada campo declarado pelo fornecedor. */
  readonly bookingFieldDefaults?: Readonly<Record<string, string>>;
  /**
   * O retrato das opções, salvo junto com o mapeamento.
   *
   * Vem da tela e não é buscado no servidor de novo de propósito: quem acabou de
   * ler os calendários já tem a lista na mão, e uma segunda leitura poderia trazer
   * outra — deixando o padrão apontando para uma opção que não está no retrato.
   */
  readonly bookingFieldOptions?: Readonly<Record<string, readonly BookingFieldOption[]>>;
  /**
   * Os segredos do fornecedor, por nome de campo — `clientId`, `clientSecret`.
   *
   * **Quais campos** é o adaptador que declara (`AgendaIntegration.platforms`), e não
   * este contrato: a Docplanner usa OAuth2 `client_credentials` ✅, e o próximo
   * fornecedor pode usar outra coisa.
   *
   * Entram cifrados e **nunca voltam**: nenhuma leitura de vínculo os expõe, e o
   * único caminho de saída é dentro do servidor. Um `GET` que os devolvesse poria a
   * credencial no log de rede do navegador.
   *
   * Campo em branco é ignorado, e não apaga o que já está gravado.
   */
  readonly secrets?: Readonly<Record<string, string>>;
}

/**
 * Os fornecedores de agenda que existem. Cresce com o registro de adaptadores.
 *
 * Separado de `AgendaSource` de propósito: `AgendaSource` inclui `zenda`, que é a
 * ausência de fornecedor. Juntar os dois produziria "plataforma = zenda", que não
 * tem adaptador nem credencial.
 */
export type AgendaPlatformId = 'doctoralia';

/**
 * Um calendário que existe do outro lado, para o mapeamento.
 *
 * Ele é identificado pela TRINCA `facilityId` + `externalDoctorId` + `addressId`:
 * na Docplanner não há id de calendário — o calendário **é** o endereço ✅.
 *
 * `where` traz cidade e rua porque nome de endereço repetido é comum, e sem isso a
 * lista mostra duas opções indistinguíveis. `enabled` é `false` quando o calendário
 * está desativado lá: ele aceita ser escolhido e depois recusa tudo, então a tela
 * precisa avisar antes.
 */
export interface PlatformCalendar {
  readonly facilityId: string;
  readonly facilityName: string | null;
  readonly externalDoctorId: string;
  readonly doctorName: string | null;
  readonly addressId: string;
  readonly addressName: string | null;
  readonly where: string | null;
  readonly enabled: boolean;
  /** Endereço só de teleconsulta: é o calendário do atendimento online ⚠️. */
  readonly online: boolean;
  /**
   * As opções de cada campo de escolha, por nome de campo.
   *
   * Genérico de propósito: quais campos existem é o adaptador que declara
   * (`AgendaPlatformInfo.bookingFields`), e as opções dependem do CALENDÁRIO — os
   * tipos de atendimento são de um endereço.
   */
  readonly bookingFieldOptions: Readonly<Record<string, readonly BookingFieldOption[]>>;
}

/** Uma opção de um campo de escolha, como o fornecedor a descreve. */
export interface BookingFieldOption {
  readonly value: string;
  readonly label: string | null;
  readonly isDefault: boolean;
  /** Quanto o atendimento ocupa, quando a plataforma diz. É a duração publicada. */
  readonly durationMin?: number | null;
}

/**
 * Um campo que o fornecedor exige na hora de reservar.
 *
 * A caixa de marcação renderiza o formulário **a partir daqui**. Um fornecedor novo
 * que exija outro campo não toca a tela: ele declara, e a tela aparece.
 */
export interface BookingField {
  readonly name: string;
  readonly label: string;
  readonly type: 'choice' | 'text' | 'boolean';
  readonly required: boolean;
  readonly hint?: string;
}

/**
 * O pedido de descoberta.
 *
 * O token viaja no CORPO, e não na query string: query string vai para log de
 * acesso, referer e histórico do navegador, e este token vale para a agenda inteira
 * de um médico real. Nada aqui é persistido — a credencial só vai para o banco,
 * cifrada, quando o mapeamento é salvo.
 */
export interface PlatformDiscoveryRequest {
  readonly platform?: AgendaPlatformId;
  readonly mode?: 'simulated' | 'live';
  readonly secrets?: Readonly<Record<string, string>>;
}

/** Um campo de credencial que um fornecedor exige. A tela renderiza a partir disto. */
export interface CredentialField {
  readonly name: string;
  readonly label: string;
  /** `true` mascara o valor no campo. */
  readonly secret: boolean;
  readonly hint?: string;
}

/**
 * Um fornecedor de agenda que a tela pode oferecer.
 *
 * Vem do registro de adaptadores do servidor, e não de uma lista no front: duas
 * listas divergem, e a que divergir oferece um fornecedor que ninguém serve — ou
 * pede uma credencial que ele não usa.
 */
export interface AgendaPlatformInfo {
  readonly id: AgendaPlatformId;
  readonly label: string;
  readonly credentialFields: readonly CredentialField[];
  /** O que ele exige em cada reserva. Vazio é válido. */
  readonly bookingFields: readonly BookingField[];
}

export interface PlatformDiscovery {
  readonly platform: AgendaPlatformId;
  readonly mode: 'simulated' | 'live';
  readonly calendars: readonly PlatformCalendar[];
}

/**
 * Um conflito de sincronia, para uma pessoa resolver.
 *
 * `overlap` é a paciente que reservou na plataforma um horário já ocupado aqui. A
 * trava do banco recusou, e **a recusa é a verdade**: o horário está ocupado. O
 * que resta é ligar para ela — e é por isso que esta linha carrega nome e telefone.
 *
 * DADO DE SAÚDE: identifica a paciente. Não entra em log, trace nem evento.
 */
export interface SyncConflict {
  readonly id: Uuid;
  readonly kind: 'overlap' | 'slot_inexistente' | 'paciente_desconhecido' | 'push_recusado';
  readonly doctorId: Uuid | null;
  readonly doctorName: string | null;
  readonly localDate: string;
  readonly localTime: string;
  readonly durationMin: number | null;
  readonly patientName: string | null;
  readonly patientPhone: string | null;
  readonly detail: string | null;
  readonly createdAt: Iso;
}

/** O estado da integração de um cliente, numa chamada. */
export interface AgendaIntegration {
  readonly links: readonly CalendarLink[];
  readonly conflicts: readonly SyncConflict[];
  /**
   * Os fornecedores que a janela de configuração pode oferecer, e o que cada um
   * exige de credencial. Vem do registro de adaptadores do servidor.
   */
  readonly platforms: readonly AgendaPlatformInfo[];
  /**
   * Para onde o tráfego do modo **Real** está sendo desviado, quando está.
   *
   * `null` é o normal: `live` fala com a plataforma. Quando `AGENDA_PLATFORM_BASE_URL`
   * aponta para outro lugar — o stub do contrato, um sandbox —, a tela é **obrigada a
   * dizer**: um modo chamado "Real" apontando para `127.0.0.1` é como alguém descobre,
   * na frente de um cliente, que a integração não estava conectada. Mesma regra do
   * `ZENDA_SIMULATE_CONNECT` na conexão do WhatsApp.
   */
  readonly liveRedirectedTo: string | null;
}

/** O que uma publicação ou um pull fizeram, para a tela dizer em vez de só recarregar. */
/** Remarcar: o horario novo, e a duracao quando ela muda junto. */
export interface RescheduleRequest {
  readonly date: string;
  readonly hora: string;
  readonly durationMin?: number;
}

/** A paciente veio (`true`) ou faltou (`false`). */
export interface PresenceRequest {
  readonly present: boolean;
}

export interface SyncResult {
  readonly published: number;
  readonly pulled: number;
  readonly created: number;
  /**
   * Reservas que entraram **sobrepondo** outra: o médico encaixou as duas.
   *
   * Separado de `created` porque não é a mesma notícia. Uma consulta nova é
   * rotina; duas pessoas no mesmo horário é algo que a atendente talvez queira
   * conferir — mesmo sendo legítimo, e é legítimo. Ver
   * [ADR 0017](../../../docs/adr/0017-a-agenda-de-um-cliente-tem-um-dono.md).
   */
  readonly squeezedIn: number;
  readonly conflicts: number;
  readonly errors: readonly string[];
}

// ── Agendamento a partir da conversa ────────────────────────────────────────

export interface AppointmentType {
  readonly id: Uuid;
  readonly code: string;
  readonly label: string;
  readonly durationMin: number;
  readonly bufferMin: number;
  readonly modality: Modality;
  /**
   * Vale para TODOS os médicos do tenant, e não só para este cliente.
   *
   * São os tipos que nasceram sem médico (o seed cria assim). A tela precisa saber
   * disso porque editar um deles pela ficha de um cliente mudaria a lista de todos os
   * outros — e ninguém que abriu a agenda de um médico espera mexer na agenda alheia.
   */
  readonly shared: boolean;
}

/** O que a tela manda para criar ou editar um tipo. `code` é derivado do rótulo. */
export interface AppointmentTypeUpsert {
  readonly label: string;
  readonly durationMin: number;
  readonly modality?: Modality;
}

/** Um horário livre, já filtrado pela preferência da paciente. */
export interface FreeSlot {
  readonly date: string;
  readonly hora: string;
  readonly minutos: number;
  readonly duracao: number;
  readonly locationId: Uuid | null;
  readonly locationName: string | null;
  readonly modality: Modality;
  /**
   * Quantas consultas cruzam este intervalo.
   *
   * Cruzam, e não "começam nele": uma consulta de 40 min às 08:00 ocupa as 08:20,
   * e contar por hora exata oferecia como livre um horário que não estava.
   */
  readonly taken: number;
  /** `true` quando já há consulta no intervalo e só entra por encaixe. */
  readonly full: boolean;
  /** Aberto por exceção, e não pela rotina. */
  readonly extra: boolean;
}

export interface FreeSlotSearch {
  readonly slots: readonly FreeSlot[];
  readonly types: readonly AppointmentType[];
  /** A duração que a busca usou. Ela define os começos possíveis. */
  readonly durationMin: number;
  /** Onde a busca parou. Serve para o "ver mais" continuar de onde ficou. */
  readonly searchedTo: string;
}

export interface BookSlot {
  readonly date: string;
  readonly hora: string;
  readonly locationId?: Uuid | null;
  /** Só quando esta consulta da série dura diferente das outras. */
  readonly durationMin?: number;
}

export interface BookRequest {
  readonly contactId: Uuid;
  readonly conversationId?: Uuid | null;
  /**
   * De quem é a agenda, quando não há conversa: paciente cadastrado à mão nunca
   * conversou. Com `conversationId`, a conversa manda.
   */
  readonly clientId?: Uuid | null;
  /** Para quem é a consulta, quando não é o próprio contato (ADR 0028). */
  readonly dependentId?: Uuid | null;
  readonly typeId?: Uuid | null;
  /** Minutos, quando não há tipo escolhido. */
  readonly durationMin?: number;
  /** Uma consulta ou a série inteira — o servidor grava tudo ou nada. */
  readonly slots: readonly BookSlot[];
  /**
   * O tema da consulta, escrito à mão: "Botox", "retorno pós-cirúrgico",
   * "primeira sessão". Vale para todas as datas da série.
   *
   * DADO DE SAÚDE: é interno. Nunca entra na mensagem enviada à paciente, nem
   * em log, trace ou evento de domínio.
   */
  readonly title?: string | null;
  /**
   * Encaixe: aceita duas consultas no mesmo intervalo.
   *
   * É **só** isso. Marcar fora do horário de rotina não precisa dele e não o
   * liga: a janela de atendimento é guia, não trava. Confundir os dois tiraria da
   * `EXCLUDE` de `appointments` toda consulta fora de rotina — a linha com
   * `overbooked` sai do índice de propósito (migração 0023) — e apagaria a
   * diferença entre "o médico abriu exceção" e "a atendente marcou às 19h".
   *
   * Fica gravado na consulta: é decisão do momento, não se deduz depois.
   */
  readonly overbook?: boolean;
  /**
   * Os valores dos campos que o fornecedor exige, por nome.
   *
   * Só vale para cliente em plataforma de terceiro, e cada ausência cai no padrão do
   * vínculo: o campo pode nem ter aparecido na tela. **Quais** campos existem é o
   * adaptador que declara.
   */
  readonly externalFields?: Readonly<Record<string, string>>;
}

/**
 * O veredito de cada horário de uma série.
 *
 * `ocupado` é o único que impede: há consulta cruzando aquele intervalo, e passar
 * por cima exige encaixe. Os dois motivos de janela **não impedem** nada — a
 * consulta fora do horário de rotina é legítima, e o veredito serve para a tela
 * avisar antes de confirmar. Ver ADR 0020.
 */
export interface SlotCheck {
  readonly date: string;
  readonly hora: string;
  /** Quantas consultas cruzam o intervalo pedido. */
  readonly taken: number;
  readonly reason:
    | 'livre'
    | 'ocupado'
    /** O médico não chega a tempo do outro endereço. Ver `agenda_settings.travel_buffer_min`. */
    | 'deslocamento'
    /** Antes da antecedência mínima combinada. Ver `agenda_settings.min_notice_minutes`. */
    | 'antecedencia'
    | 'fora_da_janela'
    | 'estoura_a_janela';
  /** Marca sem encaixe. É `taken === 0`, e independe da janela. */
  readonly free: boolean;
}

export interface SlotCheckResult {
  readonly results: readonly SlotCheck[];
}

export interface BookResult {
  readonly ids: readonly Uuid[];
  readonly seriesId: Uuid | null;
}

/** O que o painel da conversa mostra sobre a agenda daquele paciente. */
/** Um paciente achado pela busca, com os médicos a que ele está ligado. */
export interface ContactHit {
  readonly contactId: Uuid;
  readonly name: string;
  readonly phone: string | null;
  readonly clients: readonly {
    readonly clientId: Uuid;
    readonly clientName: string;
    readonly clientColor: string;
    /** A conversa com este médico, quando já existe. */
    readonly conversationId: Uuid | null;
    /** Pode receber a primeira mensagem: autorizou na ficha ou já escreveu. */
    readonly podeEnviar: boolean;
  }[];
}

export interface PatientAgenda {
  readonly upcoming: readonly {
    readonly id: Uuid;
    readonly startsAt: Iso;
    readonly date: string;
    readonly hora: string;
    readonly status: AppointmentStatus;
    readonly locationName: string | null;
    readonly title: string | null;
    readonly seriesId: Uuid | null;
    readonly overbooked: boolean;
    /** Quem é atendido, quando não é o próprio paciente (ADR 0028). */
    readonly dependentId: Uuid | null;
    readonly dependentName: string | null;
    /** De qual médico é a consulta: o mesmo paciente pode ser de vários. */
    readonly clientId: Uuid;
    readonly clientName: string;
    readonly clientColor: string;
  }[];
  /** As conversas do paciente, uma por médico com quem ele fala. */
  readonly conversations: readonly {
    readonly conversationId: Uuid;
    readonly clientId: Uuid;
    readonly clientName: string;
    readonly clientColor: string;
    readonly lastMessageAt: Iso | null;
  }[];
}

// ── Painel do dono da plataforma e rastreio de erros ───────────────────────

/**
 * Contrato do painel do dono da plataforma e do rastreio de erros.
 *
 * O administrador não é usuário de tenant: não tem `tenantId`, não usa o mesmo token e
 * não enxerga conversa, contato nem agenda (ADR 0023).
 */
export type Ambiente = 'local' | 'staging' | 'production';

export interface AmbienteInfo {
  readonly env: Ambiente;
  readonly release: string;
}

export interface AdminMe {
  readonly adminId: Uuid;
  readonly fullName: string;
  readonly email: string;
  readonly mustChangePassword: boolean;
}

export interface AdminLoginResponse {
  readonly token: string;
  readonly me: AdminMe;
}

export interface TrocarSenhaRequest {
  readonly senhaAtual: string;
  readonly senhaNova: string;
}

export interface AdminOverview {
  readonly ambiente: AmbienteInfo;
  readonly operacoes: { readonly ativas: number; readonly suspensas: number };
  readonly usuarios: {
    readonly ativos: number;
    readonly desativados: number;
    readonly aguardandoTrocaDeSenha: number;
    readonly entraramNos7Dias: number;
  };
  readonly canais: { readonly total: number; readonly porEstado: Readonly<Record<string, number>> };
  readonly qualidade: Readonly<Record<string, number>>;
  readonly problemas: { readonly abertos: number; readonly novos24h: number };
  readonly eventos24h: { readonly error: number; readonly warn: number };
}

export type TenantStatus = 'active' | 'suspended' | 'closed';

/** Uma equipe no painel da plataforma (docs/domain/equipes.md §15.3). Sem dado de paciente. */
export interface AdminEquipe {
  readonly id: Uuid;
  readonly name: string;
  readonly status: 'active' | 'archived';
  readonly createdAt: Iso;
  readonly membros: readonly {
    readonly userId: Uuid;
    readonly nome: string;
    readonly email: string;
    readonly operacao: string;
    readonly role: TeamRole;
    readonly status: 'active' | 'left' | 'removed';
  }[];
  readonly medicos: number;
  readonly pausados: number;
}

export interface AdminOperacao {
  readonly id: Uuid;
  readonly legalName: string;
  readonly cnpj: string | null;
  readonly status: TenantStatus;
  readonly createdAt: Iso;
  readonly owner: {
    readonly userId: Uuid;
    readonly fullName: string;
    readonly email: string;
  } | null;
  readonly usuarios: number;
  readonly clientes: number;
  readonly canais: {
    readonly total: number;
    readonly ativos: number;
    readonly comProblema: number;
  };
}

export interface AdminOperacaoCriar {
  readonly legalName: string;
  readonly cnpj?: string | null;
  readonly owner: { readonly fullName: string; readonly email: string };
}

export interface AdminCredencialTemporaria {
  readonly userId: Uuid;
  readonly email: string;
  /** Aparece uma vez só, nesta resposta. O banco guarda apenas o hash. */
  readonly senhaTemporaria: string;
}

export interface AdminOperacaoCriada {
  readonly operacao: AdminOperacao;
  readonly credencial: AdminCredencialTemporaria;
}

export type UserStatus = 'active' | 'disabled';

export interface AdminUsuario {
  readonly id: Uuid;
  readonly fullName: string;
  readonly email: string;
  readonly status: UserStatus;
  readonly tenantId: Uuid | null;
  readonly tenantName: string | null;
  readonly role: Role | null;
  readonly mustChangePassword: boolean;
  readonly lastLoginAt: Iso | null;
  readonly createdAt: Iso;
}

export interface AdminUsuarioCriar {
  readonly tenantId: Uuid;
  readonly fullName: string;
  readonly email: string;
  readonly role: Extract<Role, 'owner' | 'attendant'>;
}

export type ProblemaStatus = 'open' | 'resolved' | 'ignored';
export type NivelDeLog = 'warn' | 'error' | 'fatal';

export interface AdminProblema {
  readonly fingerprint: string;
  readonly level: NivelDeLog;
  readonly source: 'api' | 'web';
  readonly title: string;
  readonly status: ProblemaStatus;
  readonly firstSeenAt: Iso;
  readonly lastSeenAt: Iso;
  readonly occurrences: number;
}

export interface LinhaDeTrilha {
  readonly time: Iso;
  readonly level: string;
  readonly msg: string;
  readonly [campo: string]: unknown;
}

export interface AdminEvento {
  readonly id: Uuid;
  readonly fingerprint: string;
  readonly occurredAt: Iso;
  readonly level: string;
  readonly source: string;
  readonly message: string;
  readonly requestId: string | null;
  readonly method: string | null;
  readonly route: string | null;
  readonly statusCode: number | null;
  readonly tenantId: Uuid | null;
  readonly tenantName: string | null;
  readonly userId: Uuid | null;
  readonly release: string | null;
  readonly error: unknown;
  readonly context: Readonly<Record<string, unknown>>;
  readonly breadcrumbs: readonly LinhaDeTrilha[];
}

export interface AdminProblemaDetalhe {
  readonly problema: AdminProblema;
  readonly eventos: readonly AdminEvento[];
}

/** Um fato de negócio que não é erro: canal conectado, template aprovado, teto mudou. */
export interface AdminMarco {
  readonly id: Uuid;
  readonly occurredAt: Iso;
  readonly kind: string;
  readonly message: string;
  readonly requestId: string | null;
  readonly tenantId: Uuid | null;
  readonly tenantName: string | null;
  readonly context: Readonly<Record<string, unknown>>;
}

export interface AdminAuditoria {
  readonly id: number;
  readonly adminName: string;
  readonly action: string;
  readonly targetType: string;
  readonly targetId: string | null;
  readonly tenantName: string | null;
  readonly details: Readonly<Record<string, unknown>>;
  readonly requestId: string | null;
  readonly occurredAt: Iso;
}

export interface ClientErrorReport {
  readonly kind: 'error' | 'unhandledrejection' | 'react' | 'api';
  readonly message: string;
  readonly stack?: string;
  readonly route: string;
  readonly release?: string;
  readonly requestId?: string;
}

export const ADMIN_ROUTES = {
  login: '/api/admin/login',
  me: '/api/admin/me',
  senha: '/api/admin/senha',
  overview: '/api/admin/visao-geral',
  operacoes: '/api/admin/operacoes',
  operacao: (id: Uuid) => `/api/admin/operacoes/${id}`,
  usuarios: '/api/admin/usuarios',
  usuario: (id: Uuid) => `/api/admin/usuarios/${id}`,
  resetarSenha: (id: Uuid) => `/api/admin/usuarios/${id}/resetar-senha`,
  problemas: '/api/admin/problemas',
  problema: (fingerprint: string) => `/api/admin/problemas/${encodeURIComponent(fingerprint)}`,
  eventos: '/api/admin/eventos',
  marcos: '/api/admin/marcos',
  auditoria: '/api/admin/auditoria',
  equipes: '/api/admin/equipes',
  lideranca: (id: Uuid) => `/api/admin/equipes/${id}/lideranca`,
} as const;

export const PUBLIC_ROUTES = {
  ambiente: '/api/ambiente',
  erroCliente: '/api/telemetria/erro-cliente',
  trocarSenha: '/api/auth/senha',
} as const;

// ── Piloto automático (docs/PILOTO-AUTOMATICO.md) ──────────────────────────

export type AutopilotMode = 'off' | 'copiloto' | 'piloto';
export type AutopilotState = 'responding' | 'needs_human' | 'taken_over';

/** O que a lista mostra do piloto em cada conversa (plano §6.2). */
export interface ConversationAutopilot {
  /** O modo que vale para o médico desta conversa. */
  readonly mode: AutopilotMode;
  readonly state: AutopilotState;
  readonly reasonCode: string | null;
  /** O motivo em uma frase: "perguntou o valor do retorno". */
  readonly reason: string | null;
  readonly changedAt: Iso | null;
  /** Mensagens do paciente depois da escalação: "+2 novas". */
  readonly newSince: number;
  /** Quando a IA vai responder: "piloto responde em 1:12". */
  readonly replyDueAt: Iso | null;
  /** Alguém respondeu há pouco: o piloto reavalia a conversa nesta hora, se ninguém responder. */
  readonly waitingUntil: Iso | null;
  /** Mensagem do paciente na fila do piloto, ainda sem decisão. */
  readonly reading: boolean;
  /** Minutos esperando, contados só no horário de trabalho da atendente. */
  readonly businessMinutesWaiting: number | null;
  /** Passou do prazo que ela definiu: o tempo fica vermelho. */
  readonly overdue: boolean;
  readonly noAutopilot: boolean;
  /** Desde quando e por quê o paciente está "sem piloto" (plano §16, ficha do paciente). */
  readonly noAutopilotSince: Iso | null;
  readonly noAutopilotReason: string | null;
}

/** Uma faixa no ponto exato em que o piloto parou (plano §6.3). */
export interface AutopilotMark {
  readonly at: Iso;
  readonly state: AutopilotState;
  readonly reasonCode: string | null;
  readonly text: string;
}

export interface AutopilotDraft {
  readonly id: Uuid;
  readonly body: string;
  readonly createdAt: Iso;
}

export interface ConversationAutopilotDetail {
  readonly marks: readonly AutopilotMark[];
  readonly draft: AutopilotDraft | null;
  readonly pendingTopic: string | null;
  /**
   * A proposta feita da resposta da atendente a uma escalação por falta de informação:
   * "Adicionar à memória da Dra. Beatriz?" (plano §6.3 e §8.3).
   */
  readonly learnable: AutopilotLearning | null;
  readonly doctorLabel: string;
}

export type AutopilotPause = 'orcamento' | 'kill' | 'ia_indisponivel' | null;

/** A barra lateral e o bloco "Piloto hoje" do painel (plano §3 e §6.4). */
export interface AutopilotSummary {
  readonly mode: AutopilotMode;
  readonly needsYou: number;
  readonly takenOver: number;
  readonly pause: AutopilotPause;
  readonly providerReady: boolean;
  readonly model: string | null;
  readonly today: {
    readonly answered: number;
    readonly escalated: number;
    readonly takenOver: number;
    readonly topReason: string | null;
  };
  readonly sound: boolean;
  /** Orçamento do mês da operação: em 80 % a tela avisa, em 100 % o piloto vira copiloto. */
  readonly budget: { readonly usedBrl: number; readonly limitBrl: number } | null;
}

export interface FaixaDeHorario {
  /** 0 = domingo. */
  readonly dia: number;
  readonly inicio: string;
  readonly fim: string;
}

export interface AutopilotSettings {
  readonly mode: AutopilotMode | null;
  readonly threshold: number | null;
  /** `null` = sempre. */
  readonly replyHours: readonly FaixaDeHorario[] | null;
  readonly workHours: readonly FaixaDeHorario[] | null;
  readonly rhythm: { readonly esperaMinS: number; readonly esperaMaxS: number } | null;
  readonly minNoticeMinutes: number | null;
  readonly maxConsecutive: number | null;
  readonly humanActiveMinutes: number | null;
  readonly alertMinutes: number | null;
  readonly emailAfterMinutes: number | null;
  readonly notifyEmail: string | null;
  readonly dailySummary: boolean | null;
  readonly sound: boolean | null;
  readonly monthlyBudgetBrl: number | null;
  readonly emergencyMessage: string | null;
  readonly reserveInHours: string | null;
  readonly reserveOutHours: string | null;
}

export interface MemoryField {
  readonly id: string;
  readonly rotulo: string;
  readonly texto: string;
  readonly podeInformar: boolean;
}

export interface MemoryQa {
  readonly id: string;
  readonly pergunta: string;
  readonly resposta: string;
  readonly aprendidoEm: string | null;
}

export interface MemoryContent {
  readonly sobre: readonly MemoryField[];
  readonly perguntas: readonly MemoryQa[];
  readonly regras: readonly string[];
  readonly estilo: {
    readonly tratamento: 'voce' | 'senhor';
    readonly emoji: boolean;
    readonly assinatura: string;
    readonly formalidade: 'informal' | 'neutra' | 'formal';
    readonly frases: readonly string[];
    readonly observacoes: string;
  };
  readonly exemplos: readonly { readonly paciente: string; readonly resposta: string }[];
}

export interface MemoryVersion {
  readonly version: number;
  readonly createdAt: Iso;
  readonly createdBy: string | null;
}

export interface AutopilotLearning {
  readonly id: Uuid;
  readonly question: string;
  readonly answer: string;
  readonly createdAt: Iso;
}

export interface AutopilotTestCase {
  readonly id: Uuid;
  readonly question: string;
  readonly expected: string;
  readonly expectAction: 'responder' | 'escalar';
  readonly lastResult: {
    readonly ok: boolean;
    readonly action: string;
    readonly text: string | null;
    readonly reason: string | null;
  } | null;
  readonly lastRunAt: Iso | null;
}

/** A aba "Piloto automático" no detalhe do cliente (plano §16). */
export interface DoctorAutopilot {
  readonly doctorId: Uuid;
  readonly doctorName: string;
  /** O que a operação define e o médico herda quando não tem valor próprio. */
  readonly operation: AutopilotSettings;
  readonly own: AutopilotSettings;
  readonly effectiveMode: AutopilotMode;
  readonly memory: MemoryContent;
  readonly memoryVersion: number;
  readonly memoryTokens: number;
  readonly memoryLimit: number;
  readonly versions: readonly MemoryVersion[];
  readonly learnings: readonly AutopilotLearning[];
  readonly tests: readonly AutopilotTestCase[];
  readonly month: {
    readonly answered: number;
    readonly escalated: number;
    readonly costBrl: number;
    readonly topReasons: readonly { readonly reason: string; readonly count: number }[];
  };
  /** Acordo do modo sombra nos últimos 7 dias (plano §13). */
  readonly shadow: { readonly compared: number; readonly agreed: number };
  readonly providerReady: boolean;
  readonly quickRepliesAvailable: number;
}

export interface SimulatorTurn {
  readonly de: 'paciente' | 'atendente' | 'piloto';
  readonly texto: string;
}

export interface SimulatorResult {
  readonly action: 'nada' | 'responder' | 'reservar' | 'escalar' | 'emergencia';
  readonly text: string | null;
  readonly reason: string | null;
  readonly reasonCode: string | null;
  readonly state: AutopilotState | null;
  readonly gate: number;
  readonly category: string | null;
  readonly sources: readonly string[];
  readonly confidence: number | null;
  readonly validationFailure: string | null;
  readonly costBrl: number;
  readonly offered: readonly string[];
}

export interface AdminAutopilotRow {
  readonly tenantId: Uuid;
  readonly tenantName: string;
  readonly blocked: boolean;
  readonly decisions: number;
  readonly answered: number;
  readonly costBrl: number;
  readonly providerErrors: number;
}

export interface AdminAutopilot {
  readonly enabled: boolean;
  readonly model: string | null;
  readonly providerReady: boolean;
  readonly rows: readonly AdminAutopilotRow[];
}
